import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ShieldAlert } from 'lucide-react';
import { BaaReminder } from './BaaReminder';
import { baaApi, BAA_EVENT, BAA_SIGNED_EVENT } from './baaApi';

const PRACTICE_ROLES = ['practice_admin', 'practice_staff', 'practice'];
const PUBLIC_PREFIXES = ['/onspace', '/landing', '/welcome', '/thank-you', '/jobs', '/terms', '/privacy', '/practcom/terms', '/practcom/privacy', '/beta', '/r/', '/portal', '/p/',
  '/consent-sign', '/consent-bundle', '/public-', '/smile-design-sign', '/admin', '/reset-password', '/feedback', '/free-templates',
  '/practcom-upgrade', '/founder', '/payment'];
const DISMISS_KEY = 'baaPromptDismissed';

export const BaaGate = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const onSettings = pathname.startsWith('/practice-settings');
  const [status, setStatus] = useState(null);
  const [open, setOpen] = useState(false);
  const isPublic = PUBLIC_PREFIXES.some((p) => pathname.startsWith(p));
  const [token, setToken] = useState(localStorage.getItem('dentalToken'));
  const hasToken = !!token;

  useEffect(() => {
    const id = setInterval(() => {
      const t = localStorage.getItem('dentalToken');
      if (t !== token) {
        setToken(t);
        setStatus(null);
      }
    }, 3000);
    return () => clearInterval(id);
  }, [token]);

  const load = useCallback(async () => {
    try {
      const s = await baaApi.status();
      if (!PRACTICE_ROLES.includes(s.role)) return setStatus(null);
      setStatus(s);
      if (!s.signed && !sessionStorage.getItem(DISMISS_KEY) && !window.location.pathname.startsWith('/practice-settings')) setOpen(true);
    } catch {
      setStatus(null);
    }
  }, []);

  useEffect(() => {
    if (hasToken && !isPublic && !status) load();
  }, [hasToken, isPublic, status, load, pathname]);

  useEffect(() => {
    const show = () => {
      if (status) return setOpen(true);
      sessionStorage.removeItem(DISMISS_KEY);
      load();
    };
    const reload = () => setStatus(null);
    const hide = () => { sessionStorage.setItem(DISMISS_KEY, '1'); setOpen(false); };
    window.addEventListener('practcom:baa-hide', hide);
    window.addEventListener(BAA_EVENT, show);
    window.addEventListener(BAA_SIGNED_EVENT, reload);
    return () => {
      window.removeEventListener('practcom:baa-hide', hide);
      window.removeEventListener(BAA_EVENT, show);
      window.removeEventListener(BAA_SIGNED_EVENT, reload);
    };
  }, [status, load]);

  if (!status || isPublic || !hasToken) return null;

  const onOpenChange = (v) => {
    if (!v && !status.signed) sessionStorage.setItem(DISMISS_KEY, '1');
    setOpen(v);
  };
  const goSign = () => {
    setOpen(false);
    if (onSettings) document.getElementById('baa')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    else navigate('/practice-settings#baa');
  };

  return (
    <>
      {!status.signed && (
        <div data-testid="baa-required-banner" className="sticky top-0 z-[60] bg-amber-500 text-amber-950">
          <div className="max-w-7xl mx-auto px-4 py-2.5 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
            <ShieldAlert className="w-5 h-5 shrink-0" />
            <span className="font-semibold">Please sign your HIPAA Business Associate Agreement (BAA) to send patient documents.</span>
            <span className="hidden md:inline text-amber-900">Dictate Visit, portal links, texts and emails to patients are paused until it's signed.</span>
            <button type="button" onClick={() => (status.canSign ? goSign() : setOpen(true))} data-testid="baa-banner-open-btn"
              className="ml-auto rounded-full bg-[#0b2545] text-white px-4 py-1.5 text-xs font-bold hover:bg-[#13315c] transition-colors">
              {status.canSign ? 'Sign BAA in Practice Settings' : 'Learn more'}
            </button>
          </div>
        </div>
      )}
      <BaaReminder open={open && !status.signed} onOpenChange={onOpenChange} canSign={status.canSign} onGo={goSign} />
    </>
  );
};
